import { useCallback, useEffect, useRef, useState } from "react";

import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Modal,
    TextField,
    Typography,
} from "@mui/material";

import { Add, Close, CloudUpload, Delete, Search } from "@mui/icons-material";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

import {
    MDXEditor,
    Button as MDXButton,
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    ChangeCodeMirrorLanguage,
    CodeToggle,
    ConditionalContents,
    CreateLink,
    DiffSourceToggleWrapper,
    InsertCodeBlock,
    InsertTable,
    InsertThematicBreak,
    ListsToggle,
    Separator,
    UndoRedo,
    StrikeThroughSupSubToggles,
    codeBlockPlugin,
    codeMirrorPlugin,
    diffSourcePlugin,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";

import ArrowOnboarding from "../static/ic_arrow.png";
import editorTranslations from "../static/editor-translations.json";
import "./Forms.css";

const ContentsPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [open, setOpen] = useState(false);
    const [contentId, setContentId] = useState(null);

    const [validate, setValidate] = useState(false);
    const [contentTitle, setContentTitle] = useState("");
    const [contentDescription, setContentDescription] = useState("");
    const [currentContentText, setCurrentContentText] = useState("");
    const [newContentText, setNewContentText] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState(false);
    const contentEditorRef = useRef(null);

    const [contents, setContents] = useState([]);
    const [deleteId, setDeleteId] = useState(null);
    const [searchFilter, setSearchFilter] = useState("");

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file && file.type === "application/pdf") {
            setFileError(false);
            setSelectedFile(file);

            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${backendEndpoint}/contents/pdf/`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: formData,
            });

            const { text } = await response.json();

            setNewContentText(text);
        } else {
            setFileError(true);
            setSelectedFile(null);
            setNewContentText("");
        }
    };

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleOpen = (isNew = true) => {
        if (isNew) {
            setContentId(null);
            setContentTitle("");
            setContentDescription("");
            setCurrentContentText("");
            setNewContentText("");
        }
        setValidate(false);
        setOpen(true);
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const createContent = useCallback(
        async (contentData) => {
            const response = await fetch(`${backendEndpoint}/contents/`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(contentData),
            });

            const json = await response.json();
            return json;
        },
        [backendEndpoint, getCookieValue],
    );

    const updateContent = useCallback(
        async (contentId, contentData) => {
            await fetch(`${backendEndpoint}/contents/${contentId}/`, {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(contentData),
            });
        },
        [backendEndpoint, getCookieValue],
    );

    const deleteContent = useCallback(
        async (contentId) => {
            await fetch(`${backendEndpoint}/contents/${contentId}/`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            });
        },
        [backendEndpoint, getCookieValue],
    );

    const handleOk = useCallback(async () => {
        setValidate(true);

        if (contentTitle === "" || newContentText === "") {
            return;
        }

        const newContent = {
            id: contentId,
            title: contentTitle,
            description: contentDescription,
            text: newContentText,
            origin: "text",
        };

        let newContents = [...contents];

        if (contentId === null) {
            const { id: newId } = await createContent(newContent);
            newContents.push({ ...newContent, id: newId });
        } else {
            await updateContent(contentId, newContent);
            newContents = newContents.map((content) =>
                contentId === content.id ? newContent : content,
            );
        }

        setContents(newContents);
        handleClose();
    }, [
        contentDescription,
        newContentText,
        contentTitle,
        contents,
        contentId,
        createContent,
        updateContent,
        handleClose,
    ]);

    const handleDeleteContent = async () => {
        if (deleteId === null) return;

        await deleteContent(deleteId);
        const newContents = contents.filter(
            (content) => content.id !== deleteId,
        );
        setContents(newContents);

        setDeleteId(null);
    };

    const handleEditContent = (id) => {
        const { title, description, text } = contents.find(
            (content) => content.id === id,
        );

        setContentId(id);
        setContentTitle(title);
        setContentDescription(description);
        setCurrentContentText(text);
        setNewContentText(text);
        handleOpen(false);
    };

    const stripMarkdown = (text) => {
        if (!text) return "";
        return text
            .replace(/```[\s\S]*?```/g, "")
            .replace(/`[^`]*`/g, "")
            .replace(/!\[.*?\]\(.*?\)/g, "")
            .replace(/\[.*?\]\(.*?\)/g, "")
            .replace(/^#{1,6}\s+/gm, "")
            .replace(/^\s*[-*+]\s+/gm, "")
            .replace(/^\s*\d+\.\s+/gm, "")
            .replace(/^\s*>\s+/gm, "")
            .replace(/(\*\*|__)(.*?)\1/g, "$2")
            .replace(/(\*|_)(.*?)\1/g, "$2")
            .replace(/~~(.*?)~~/g, "$1")
            .replace(/---+/g, "")
            .replace(/\n{2,}/g, " ")
            .replace(/\n/g, " ")
            .trim();
    };

    const stripAccentsAndLowerCase = (str) => {
        return str?.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase() || "";
    };

    const getSnippet = (content, term) => {
        if (!term.trim()) {
            return stripMarkdown(content.description) || "";
        }
        const normalizedTerm = stripAccentsAndLowerCase(term);
        const sources = [content.description, content.text].filter(Boolean);
        for (const src of sources) {
            const lines = src.split("\n");
            for (const line of lines) {
                const cleanLine = stripMarkdown(line);
                if (!cleanLine) continue;
                const idx =
                    stripAccentsAndLowerCase(cleanLine).indexOf(normalizedTerm);
                if (idx === -1) continue;
                return (
                    <>
                        {cleanLine.slice(0, idx)}
                        <span style={{ color: "#0359D6", fontWeight: 600 }}>
                            {cleanLine.slice(idx, idx + term.length)}
                        </span>
                        {cleanLine.slice(idx + term.length)}
                    </>
                );
            }
        }
        return stripMarkdown(content.description) || "";
    };

    const handleOnLoad = useCallback(async () => {
        const response = await fetch(`${backendEndpoint}/contents/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const json = await response.json();
        const contents = json.contents;

        setContents(contents);
        /*
        if(document.getElementById('divOnboardingConteudo') != null)
            setTimeout(function (){document.getElementById('divOnboardingConteudo').style.display="block";}, 2000);
*/
    }, [backendEndpoint, getCookieValue]);

    useEffect(() => {
        handleOnLoad();
    }, [handleOnLoad]);

    return (
        <Box sx={{ width: "100%" }} id="contents-container">
            {/*}
            <Typography variant="h4" gutterBottom
                    sx={{
                        color: "#0359D6",
                        paddingTop: "10px !important",
                    }}
            >
                Treinamento do Agente
            </Typography>
            */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    margin: "10px 15px 50px 15px",
                }}
            >
                <Box
                    variant="h4"
                    sx={{
                        width: "150",
                        color: "#333",
                        fontSize: "24px",
                        fontWeight: "500",
                    }}
                >
                    Treinamento do agente
                    <Box
                        variant="div"
                        sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            pr: "10px",
                        }}
                    >
                        Adicione manuais, histórico de conversas, bases de
                        conhecimento e documentos que o Chiko precisa conhecer
                        para fazer o melhor atendimento
                    </Box>
                </Box>
                <Button
                    id="add-content-btn"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{
                        marginBottom: 2,
                        borderRadius: "25px",
                        padding: "8px 16px 6px",
                        textTransform: "none",
                    }}
                >
                    Adicionar novo conteúdo
                </Button>
            </Box>
            {/*}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    id="add-content-btn"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{ marginBottom: 2 }}
                >
                    Adicionar
                </Button>
            </Box>
            */}

            {contents.length === 0 ? (
                <>
                    <Box
                        sx={{
                            position: "Absolute",
                            top: "100px",
                            right: "150px",
                            display: "block",
                        }}
                        id="divOnboardingConteudo"
                    >
                        Vamos começar a treinar o Chiko? Clique aqui
                        &nbsp;&nbsp;
                        <img
                            src={ArrowOnboarding}
                            alt="Chiko Logo"
                            height="80"
                        />
                    </Box>
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            width: "100%",
                            mb: 2,
                        }}
                    >
                        <TextField
                            placeholder="Buscar por título, descrição ou conteúdo..."
                            variant="outlined"
                            size="small"
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <Search sx={{ color: "#999", mr: 1 }} />
                                    ),
                                },
                            }}
                            sx={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                            }}
                        />
                    </Box>
                    <List
                        id="contents-list"
                        sx={{
                            width: "100%",
                            // backgroundColor: "#eeeeee",
                            // height: 400,
                            borderRadius: 1,
                            overflowY: "auto",
                            border: "1px solid #eeeeee",
                            paddingTop: "0",
                            paddingBottom: "0",
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: "#E2E8F0",
                                padding: "14px",
                                fontWeight: "500",
                            }}
                        >
                            Conteúdos adicionados
                        </Box>
                        {contents
                            .filter((content) => {
                                if (!searchFilter.trim()) return true;
                                const term =
                                    stripAccentsAndLowerCase(searchFilter);
                                return (
                                    stripAccentsAndLowerCase(
                                        content.title,
                                    ).includes(term) ||
                                    stripAccentsAndLowerCase(
                                        content.description,
                                    ).includes(term) ||
                                    stripAccentsAndLowerCase(
                                        content.text,
                                    ).includes(term)
                                );
                            })
                            .map((content) => (
                                <ListItem
                                    key={content.id}
                                    onClick={() =>
                                        handleEditContent(content.id)
                                    }
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        ":hover": {
                                            backgroundColor: "#f8f3f3ff",
                                            cursor: "pointer",
                                            color: "#000",
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#263145",
                                                    mb: 0.3,
                                                }}
                                            >
                                                {content.title}
                                            </Box>
                                        }
                                        secondary={
                                            <Box
                                                sx={{
                                                    fontSize: "12px",
                                                    color: "#555",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {searchFilter.trim()
                                                    ? getSnippet(
                                                          content,
                                                          searchFilter,
                                                      )
                                                    : content.description || ""}
                                            </Box>
                                        }
                                    />

                                    <Box>
                                        {/*
                                <IconButton
                                    edge="end"
                                    aria-label="edit"                               
                                    onClick={() => handleEditContent(content.id)}
                                >
                                    <Edit />
                                </IconButton>
                                */}
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteId(content.id);
                                            }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </ListItem>
                            ))}
                    </List>
                </>
            )}

            <ConfirmDeleteModal
                open={deleteId !== null}
                onClose={() => {
                    setDeleteId(null);
                }}
                onConfirm={() => handleDeleteContent()}
            />

            {/* Content modal */}
            <Modal
                open={open}
                // onClose={handleClose}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box
                    id="contents-modal-container"
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 880,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">
                            {contentId === null
                                ? "Novo conteúdo"
                                : "Editar conteúdo"}
                        </Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        <Box className="formLabel">Título*</Box>

                        <TextField
                            value={contentTitle}
                            onChange={(e) => setContentTitle(e.target.value)}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            error={
                                validate && contentTitle === "" ? true : false
                            }
                            helperText={
                                validate && contentTitle === ""
                                    ? "Preencha o título"
                                    : ""
                            }
                            className="inputs"
                        />

                        <Box className="formLabel">Descrição</Box>

                        <TextField
                            value={contentDescription}
                            onChange={(e) =>
                                setContentDescription(e.target.value)
                            }
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            className="inputs"
                            sx={{
                                border: "0px",
                            }}
                        />

                        <div id="content-field" ref={contentEditorRef}>
                            <Box className="formLabel">Conteúdo*</Box>

                            <MDXEditor
                                markdown={newContentText}
                                onChange={setNewContentText}
                                className={
                                    validate && newContentText === ""
                                        ? "error"
                                        : ""
                                }
                                plugins={[
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                    tablePlugin(),
                                    thematicBreakPlugin(),
                                    linkPlugin(),
                                    linkDialogPlugin(),
                                    markdownShortcutPlugin(),
                                    diffSourcePlugin({
                                        diffMarkdown: currentContentText,
                                        viewMode: "rich-text",
                                    }),
                                    codeBlockPlugin({
                                        defaultCodeBlockLanguage: "text",
                                    }),
                                    codeMirrorPlugin({
                                        codeBlockLanguages: {
                                            text: "Texto",
                                            json: "JSON",
                                            js: "JavaScript",
                                            python: "Python",
                                            php: "PHP",
                                            java: "Java",
                                        },
                                    }),
                                    toolbarPlugin({
                                        toolbarClassName: "content-tool-bar",
                                        toolbarContents: () => (
                                            <ConditionalContents
                                                options={[
                                                    {
                                                        when: (editor) =>
                                                            editor?.editorType ===
                                                            "codeblock",
                                                        contents: () => (
                                                            <ChangeCodeMirrorLanguage />
                                                        ),
                                                    },
                                                    {
                                                        fallback: () => (
                                                            <>
                                                                <BlockTypeSelect />
                                                                <BoldItalicUnderlineToggles />
                                                                <CodeToggle />

                                                                <Separator />
                                                                <StrikeThroughSupSubToggles />

                                                                <Separator />
                                                                <ListsToggle />

                                                                <Separator />
                                                                <CreateLink />
                                                                <InsertTable />
                                                                <InsertThematicBreak />
                                                                <InsertCodeBlock />

                                                                <Separator />
                                                                <DiffSourceToggleWrapper />
                                                                <UndoRedo />
                                                                <FullScreenButton
                                                                    containerRef={
                                                                        contentEditorRef
                                                                    }
                                                                />
                                                            </>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        ),
                                    }),
                                ]}
                                translation={(
                                    key,
                                    defaultValue,
                                    interpolations,
                                ) => {
                                    try {
                                        const translatedValue = key
                                            .split(".")
                                            .reduce(
                                                (obj, key) => obj?.[key],
                                                editorTranslations,
                                            );

                                        const interpolatedValue =
                                            translatedValue.replace(
                                                /{{(.*?)}}/g,
                                                (match, key) => {
                                                    return (
                                                        interpolations[
                                                            key.trim()
                                                        ] || match
                                                    );
                                                },
                                            );
                                        return interpolatedValue;
                                    } catch {
                                        return defaultValue;
                                    }
                                }}
                            />
                            {validate && newContentText === "" ? (
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        mx: "14px",
                                        mt: "3px",
                                        color: "#d32f2f",
                                    }}
                                >
                                    Preencha o conteúdo
                                </Typography>
                            ) : null}
                        </div>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                flexDirection: "row",
                                margin: "10px 15px",
                            }}
                        >
                            <Box>
                                <Button
                                    id="contents-pdf-btn"
                                    variant="outlined"
                                    component="label"
                                    color="primary"
                                    sx={{ marginTop: 2 }}
                                    startIcon={<CloudUpload />}
                                >
                                    Conteúdo em PDF
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                {fileError && (
                                    <Typography variant="body2" color="error">
                                        Por favor, faça upload de um arquivo PDF
                                        válido.
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setFileError(false);
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Typography>
                                )}
                                {selectedFile && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        PDF Selecionado: {selectedFile.name}{" "}
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setNewContentText("");
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Typography>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <Button
                                    onClick={handleClose}
                                    variant="contained"
                                    sx={{
                                        marginLeft: "auto",
                                        marginRight: "10px",
                                        marginTop: 2,
                                        backgroundColor: "#eee",
                                        color: "#253044",
                                    }}
                                >
                                    Cancelar
                                </Button>

                                <Button
                                    id="create-content-btn"
                                    onClick={handleOk}
                                    variant="contained"
                                    sx={{
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        marginTop: 2,
                                    }}
                                >
                                    Salvar e treinar...
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

function FullScreenButton({ containerRef }) {
    const toggle = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    return (
        <MDXButton onClick={toggle} title="Alternar Tela Cheia">
            ⛶
        </MDXButton>
    );
}

export default ContentsPage;
